import { useEffect, useRef, useState } from 'react';

interface QRCountdownProps {
  /** Base64 PNG image data (without data:image prefix) */
  image: string;
  /** Seconds until this QR expires */
  ttl: number;
  /** Which QR code in the session (1-6) */
  index: number;
  /** Total QR codes per session */
  total: number;
}

export function QRCountdown({ image, ttl, index, total }: QRCountdownProps) {
  const [remaining, setRemaining] = useState(ttl);
  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);

  // Reset countdown when TTL or image changes (new QR)
  useEffect(() => {
    setRemaining(ttl);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [ttl, image]);

  const progress = ttl > 0 ? remaining / ttl : 0;

  // Color shifts: accent -> amber -> red
  const barColor =
    remaining <= 5 ? '#ef4444' : remaining <= 10 ? '#d97706' : '#6366f1';

  return (
    <div className="flex flex-col items-center gap-3">
      {/* QR code image */}
      <img
        src={`data:image/png;base64,${image}`}
        alt="WhatsApp QR Code"
        className="w-[240px] h-[240px] rounded-xl border border-edge shadow-sm"
      />

      {/* Progress bar */}
      <div className="w-[240px]">
        <div className="h-1 w-full rounded-full bg-surface-raised overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress * 100}%`,
              backgroundColor: barColor,
              transition: 'width 1s linear, background-color 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Countdown text */}
      <div className="text-center">
        <p className="text-sm text-ink-secondary">
          Expires in{' '}
          <span
            className="font-semibold"
            style={{ color: barColor }}
          >
            {remaining}s
          </span>
        </p>
        <p className="text-xs text-ink-tertiary mt-0.5">
          QR code {index} of {total} &mdash; refreshes automatically
        </p>
      </div>
    </div>
  );
}
